/**
 * Premium step icons for the pet-loss readiness tool, styled in
 * Paction's brand palette with a cohesive, modern design language.
 */

type IconProps = { className?: string };

export function TimingIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Soft background circle */}
      <circle cx="60" cy="60" r="52" fill="#559A9D" opacity="0.12" />

      {/* Clock circle */}
      <circle
        cx="60"
        cy="60"
        r="36"
        fill="#559A9D"
        opacity="0.08"
        stroke="#559A9D"
        strokeWidth="2"
      />

      {/* Clock face */}
      <circle
        cx="60"
        cy="60"
        r="32"
        fill="none"
        stroke="#01686C"
        strokeWidth="2.5"
      />

      {/* Hour markers */}
      <circle cx="60" cy="32" r="1.5" fill="#01686C" />
      <circle cx="88" cy="60" r="1.5" fill="#01686C" />
      <circle cx="60" cy="88" r="1.5" fill="#01686C" />
      <circle cx="32" cy="60" r="1.5" fill="#01686C" />

      {/* Hour hand */}
      <line
        x1="60"
        y1="60"
        x2="60"
        y2="42"
        stroke="#01686C"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      {/* Minute hand */}
      <line
        x1="60"
        y1="60"
        x2="76"
        y2="60"
        stroke="#ED846F"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Center dot */}
      <circle cx="60" cy="60" r="3" fill="#01686C" />
    </svg>
  );
}

export function EmotionsIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Soft background circle */}
      <circle cx="60" cy="60" r="52" fill="#ED846F" opacity="0.12" />

      {/* Heart outline */}
      <path
        d="M60 105 C35 85 20 70 20 54 C20 42 28 34 38 34 C46 34 53 40 60 48 C67 40 74 34 82 34 C92 34 100 42 100 54 C100 70 85 85 60 105Z"
        fill="none"
        stroke="#ED846F"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />

      {/* Inner heart fill - subtle */}
      <path
        d="M60 100 C38 82 26 70 26 56 C26 46 33 38 42 38 C49 38 55 43 60 50 C65 43 71 38 78 38 C87 38 94 46 94 56 C94 70 82 82 60 100Z"
        fill="#ED846F"
        opacity="0.2"
      />

      {/* Highlight accent */}
      <ellipse cx="45" cy="50" rx="5" ry="6" fill="#ED846F" opacity="0.4" />
    </svg>
  );
}

export function MotivationIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Soft background circle */}
      <circle cx="60" cy="60" r="52" fill="#559A9D" opacity="0.12" />

      {/* Lightbulb body */}
      <path
        d="M60 28 C48 28 40 36 40 48 C40 56 44 62 48 68 L72 68 C76 62 80 56 80 48 C80 36 72 28 60 28Z"
        fill="none"
        stroke="#01686C"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />

      {/* Lightbulb fill - inner glow */}
      <path
        d="M60 32 C50 32 44 38 44 48 C44 55 47 61 51 67 L69 67 C73 61 76 55 76 48 C76 38 70 32 60 32Z"
        fill="#ED846F"
        opacity="0.2"
      />

      {/* Base threads */}
      <line x1="52" y1="68" x2="52" y2="74" stroke="#01686C" strokeWidth="2" />
      <line x1="60" y1="68" x2="60" y2="74" stroke="#01686C" strokeWidth="2" />
      <line x1="68" y1="68" x2="68" y2="74" stroke="#01686C" strokeWidth="2" />

      {/* Base cap */}
      <rect x="50" y="74" width="20" height="4" rx="1" fill="#01686C" />

      {/* Light rays accent */}
      <path
        d="M30 40 L38 40"
        stroke="#ED846F"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.6"
      />
      <path
        d="M90 40 L82 40"
        stroke="#ED846F"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.6"
      />
      <path
        d="M32 55 L40 55"
        stroke="#ED846F"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.6"
      />
      <path
        d="M88 55 L80 55"
        stroke="#ED846F"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.6"
      />
    </svg>
  );
}

export function HouseholdIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Soft background circle */}
      <circle cx="60" cy="60" r="52" fill="#C5B29F" opacity="0.12" />

      {/* House outline */}
      <path
        d="M60 28 L96 54 L92 54 L92 92 L28 92 L28 54 L24 54 Z"
        fill="none"
        stroke="#01686C"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />

      {/* House fill - subtle */}
      <path
        d="M60 32 L92 54 L88 54 L88 92 L32 92 L32 54 L28 54 Z"
        fill="#C5B29F"
        opacity="0.15"
      />

      {/* Door */}
      <rect
        x="50"
        y="64"
        width="16"
        height="24"
        rx="1.5"
        fill="none"
        stroke="#01686C"
        strokeWidth="2"
      />
      <circle cx="64" cy="76" r="1.5" fill="#01686C" />

      {/* Left window */}
      <rect
        x="35"
        y="60"
        width="10"
        height="10"
        rx="1"
        fill="none"
        stroke="#559A9D"
        strokeWidth="1.5"
      />
      <line x1="40" y1="60" x2="40" y2="70" stroke="#559A9D" strokeWidth="1" />
      <line x1="35" y1="65" x2="45" y2="65" stroke="#559A9D" strokeWidth="1" />

      {/* Right window */}
      <rect
        x="75"
        y="60"
        width="10"
        height="10"
        rx="1"
        fill="none"
        stroke="#559A9D"
        strokeWidth="1.5"
      />
      <line x1="80" y1="60" x2="80" y2="70" stroke="#559A9D" strokeWidth="1" />
      <line x1="75" y1="65" x2="85" y2="65" stroke="#559A9D" strokeWidth="1" />

      {/* Roof accent */}
      <path
        d="M50 48 L60 38 L70 48"
        fill="none"
        stroke="#ED846F"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.6"
      />
    </svg>
  );
}

export function NextStepsIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Soft background circle */}
      <circle cx="60" cy="60" r="52" fill="#01686C" opacity="0.12" />

      {/* Path line */}
      <path
        d="M30 60 Q60 40 90 60"
        fill="none"
        stroke="#559A9D"
        strokeWidth="3"
        strokeLinecap="round"
        opacity="0.5"
      />

      {/* Arrow line */}
      <path
        d="M30 60 L90 60"
        fill="none"
        stroke="#01686C"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      {/* Arrow head */}
      <path
        d="M85 55 L92 60 L85 65"
        fill="none"
        stroke="#ED846F"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Step circles */}
      <circle
        cx="30"
        cy="60"
        r="5"
        fill="none"
        stroke="#01686C"
        strokeWidth="2"
      />
      <circle cx="30" cy="60" r="2.5" fill="#01686C" />

      <circle
        cx="60"
        cy="60"
        r="4"
        fill="none"
        stroke="#559A9D"
        strokeWidth="2"
        opacity="0.7"
      />

      <circle
        cx="90"
        cy="60"
        r="5"
        fill="none"
        stroke="#ED846F"
        strokeWidth="2"
      />
      <circle cx="90" cy="60" r="2.5" fill="#ED846F" />
    </svg>
  );
}
